/**
 * NQ Scalper signals.
 *
 * Bollinger bands match the Pine script: 10-bar simple average, 1.5 times
 * the population standard deviation (Pine `ta.stdev` divides by N, not N-1),
 * and the current bar is inside the window. A 10-point close prints the hollow
 * star. A 15-point close prints the filled star and leaves the hollow one off.
 * The check is the running close while the
 * minute is open, and the official close once Databento publishes the minute.
 * Stars print only at or after 9:40 ET on that bar's Chicago calendar date.
 * Closes before the gate stay in the Bollinger window.
 */

export const BB_LENGTH = 10;
export const BB_MULT = 1.5;
export const OFFSET_10 = 10;
export const OFFSET_15 = 15;

const MINUTE_MS = 60_000;
const ET = 'America/New_York';
const CHICAGO = 'America/Chicago';

export type StarSide = 'long' | 'short';
export type StarTier = 10 | 15;

export interface ScalperBar {
  minute: number;
  close: number;
}

export interface ScalperStar {
  minute: number;
  side: StarSide;
  tier: StarTier;
  provisional: boolean;
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 Sunday … 6 Saturday. */
  weekday: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const zonedFormatters = new Map<string, Intl.DateTimeFormat>();

export function minuteKey(ms: number): number {
  return Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

function zonedFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = zonedFormatters.get(timeZone);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
  });
  zonedFormatters.set(timeZone, created);
  return created;
}

const zonedPartCache = new Map<string, ZonedParts>();

function zonedParts(ms: number, timeZone: string): ZonedParts {
  const key = `${timeZone}|${Math.floor(ms / MINUTE_MS)}`;
  const cached = zonedPartCache.get(key);
  if (cached) return cached;
  const bag: Record<string, string> = {};
  const formatted = zonedFormatter(timeZone).formatToParts(new Date(ms));
  for (const part of formatted) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  let hour = Number(bag.hour);
  if (hour === 24) hour = 0;
  const clock: ZonedParts = {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    hour,
    minute: Number(bag.minute),
    weekday: WEEKDAYS.indexOf(bag.weekday),
  };
  if (zonedPartCache.size > 100_000) zonedPartCache.clear();
  zonedPartCache.set(key, clock);
  return clock;
}

function etOffsetMinutes(utcMs: number): number {
  const parts = zonedParts(utcMs, ET);
  const wall = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  const floored = Math.floor(utcMs / MINUTE_MS) * MINUTE_MS;
  return Math.round((wall - floored) / MINUTE_MS);
}

/** Wall-clock America/New_York time as a UTC millisecond timestamp. */
export function etWallToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number {
  let utc = Date.UTC(year, month - 1, day, hour, minute);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const next = Date.UTC(year, month - 1, day, hour, minute) - etOffsetMinutes(utc) * MINUTE_MS;
    if (next === utc) return utc;
    utc = next;
  }
  return utc;
}

/** CME equity Globex is open. Daily halt is 17:00–18:00 ET, and the week ends Friday 17:00 ET. */
export function globexOpen(ms: number): boolean {
  const parts = zonedParts(ms, ET);
  const mins = parts.hour * 60 + parts.minute;
  if (parts.weekday === 6) return false;
  if (parts.weekday === 0 && mins < 18 * 60) return false;
  if (parts.weekday === 5 && mins >= 17 * 60) return false;
  if (parts.weekday >= 1 && parts.weekday <= 4 && mins >= 17 * 60 && mins < 18 * 60) {
    return false;
  }
  return true;
}

function sessionOpenMs(insideSessionMs: number): number {
  const parts = zonedParts(insideSessionMs, ET);
  const mins = parts.hour * 60 + parts.minute;
  if (mins >= 18 * 60) {
    return etWallToUtc(parts.year, parts.month, parts.day, 18, 0);
  }
  const yesterday = zonedParts(
    etWallToUtc(parts.year, parts.month, parts.day, 12, 0) - 24 * 60 * MINUTE_MS,
    ET,
  );
  return etWallToUtc(yesterday.year, yesterday.month, yesterday.day, 18, 0);
}

/** The latest 17:00 ET cash close at or before `nowMs`. */
function latestCashCloseMs(nowMs: number): number {
  const parts = zonedParts(nowMs, ET);
  const mins = parts.hour * 60 + parts.minute;
  let cursor = etWallToUtc(parts.year, parts.month, parts.day, 17, 0);
  if (mins < 17 * 60) cursor -= 24 * 60 * MINUTE_MS;
  for (let step = 0; step < 6; step += 1) {
    const probed = zonedParts(cursor, ET);
    if (probed.weekday >= 1 && probed.weekday <= 5) {
      return etWallToUtc(probed.year, probed.month, probed.day, 17, 0);
    }
    cursor -= 24 * 60 * MINUTE_MS;
  }
  return cursor;
}

/**
 * Pine `time_allowed`. `year` / `month` / `dayofmonth` are the NQ chart's
 * Chicago calendar date, and the gate is 9:40 America/New_York on that date.
 * The 9:40 bar is allowed. The 9:39 bar is not. Sunday evening is allowed
 * because that Chicago date's 9:40 has already passed.
 */
export function signalAllowed(ms: number): boolean {
  const chicago = zonedParts(ms, CHICAGO);
  const start = etWallToUtc(chicago.year, chicago.month, chicago.day, 9, 40);
  return ms >= start;
}

/**
 * The session to draw. Cash hours use the live Globex window. From the 17:00
 * close until the next 09:30, keep the session that just finished, because
 * the selected ticker has no overnight candles.
 */
export function sessionWindow(nowMs: number): { startMs: number; endMs: number } {
  const parts = zonedParts(nowMs, ET);
  const mins = parts.hour * 60 + parts.minute;
  const cashOpen =
    parts.weekday >= 1 && parts.weekday <= 5 && mins >= 9 * 60 + 30 && mins < 17 * 60;
  if (cashOpen) {
    return { startMs: sessionOpenMs(nowMs), endMs: nowMs };
  }
  const endMs = latestCashCloseMs(nowMs);
  return { startMs: sessionOpenMs(endMs - MINUTE_MS), endMs };
}

/** ET date of the cash session inside this Globex window, `YYYY-MM-DD`. */
export function cashSessionDate(nowMs: number): string {
  const session = sessionWindow(nowMs);
  const probe = zonedParts(session.startMs + 16 * 60 * MINUTE_MS, ET);
  const month = String(probe.month).padStart(2, '0');
  const day = String(probe.day).padStart(2, '0');
  return `${probe.year}-${month}-${day}`;
}

interface Extension {
  side: StarSide;
  tier: StarTier;
}

/** Stars for one close, using that close as the last value in `closes`. */
export function extensionStars(closes: number[]): Extension[] {
  if (closes.length < BB_LENGTH) return [];
  const window = closes.slice(closes.length - BB_LENGTH);
  const mean = window.reduce((sum, value) => sum + value, 0) / BB_LENGTH;
  const variance =
    window.reduce((sum, value) => sum + (value - mean) ** 2, 0) / BB_LENGTH;
  const deviation = Math.sqrt(variance);
  const upper = mean + BB_MULT * deviation;
  const lower = mean - BB_MULT * deviation;
  const close = window[window.length - 1];
  const stars: Extension[] = [];
  if (close > upper + OFFSET_15) stars.push({ side: 'short', tier: 15 });
  else if (close > upper + OFFSET_10) stars.push({ side: 'short', tier: 10 });
  if (close < lower - OFFSET_15) stars.push({ side: 'long', tier: 15 });
  else if (close < lower - OFFSET_10) stars.push({ side: 'long', tier: 10 });
  return stars;
}

/**
 * Stars for closed minutes plus the minute that is still forming.
 *
 * `forming` is ignored when that minute is already in `closed` — the official
 * close is the one that holds. A forming star is dropped by the next call
 * when the running close comes back inside the band.
 */
export function scalperStars(closed: ScalperBar[], forming: ScalperBar | null): ScalperStar[] {
  const series = closed
    .filter((bar) => globexOpen(bar.minute))
    .slice()
    .sort((a, b) => a.minute - b.minute);
  const formingHeld =
    forming !== null &&
    globexOpen(forming.minute) &&
    !series.some((bar) => bar.minute === forming.minute)
      ? forming
      : null;
  const bars = formingHeld ? [...series, formingHeld] : series;
  const closes = bars.map((bar) => bar.close);
  const stars: ScalperStar[] = [];
  for (let index = BB_LENGTH - 1; index < bars.length; index += 1) {
    const bar = bars[index];
    if (!signalAllowed(bar.minute)) continue;
    const windowStart = index + 1 - BB_LENGTH;
    for (const star of extensionStars(closes.slice(windowStart, index + 1))) {
      stars.push({
        minute: bar.minute,
        side: star.side,
        tier: star.tier,
        provisional: formingHeld !== null && bar.minute === formingHeld.minute,
      });
    }
  }
  return stars;
}
